import Dialog from '@mui/material/Dialog';
import { Bar } from 'react-chartjs-2';
import Directions from '../Directions';
import { useState, useMemo, useRef, useContext } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, } from 'chart.js';
import TripRateCalculationViewModel from '../../TripRate/ViewModels/TripRateCalculationViewModel';
import TripRateDay from '../../TripRate/ViewModels/TripRateDay';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import ExportTripRateGraphDialog from '../../TripRate/Calculation/ExportTripRateGraphDialog';
import ExportTripRateGraphViewModel from '../../TripRate/ViewModels/ExportTripRateGraphViewModel';
import { APIContext } from '../../Contexts/APIContext';

ChartJS.register
    (CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface IProps {
    vm: TripRateCalculationViewModel;
    onClose?: () => void;
    open: boolean;
}

const calculatePercentage = (tripRate: number, totalRate: number): number =>
    totalRate > 0 ? (tripRate / totalRate) * 100 : 0;

export default function TripRateTotalChart({ vm, onClose, open }: IProps) {
    const [direction, setDirection] = useState<string>('Total');
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const chartRef = useRef<any>(null);

    const { postPDF } = useContext(APIContext);

    const chartData = useMemo(() => {
        let selectedData: TripRateDay[] = [];
        let totalRate = 0;
        let chartTitle = '';

        switch (direction.toLowerCase()) {
            case 'arrivals':
                selectedData = vm.arrivals;
                totalRate = vm.arrivalsTotalRate || 0;
                chartTitle = `TRIP RATE GRAPH - ARRIVALS - ${vm.unit || ''}`;
                break;
            case 'departures':
                selectedData = vm.departures;
                totalRate = vm.departuresTotalRate || 0;
                chartTitle = `TRIP RATE GRAPH - DEPARTURES - ${vm.unit || ''}`;
                break;
            case 'total':
            default:
                selectedData = vm.totals;
                totalRate = vm.totalsTotalRate || 0;
                chartTitle = `TRIP RATE GRAPH - TOTALS - ${vm.unit || ''}`;
                break;
        }

        return {
            labels: selectedData.map((item) => item.period || ''),
            datasets: [
                {
                    label: 'Trip Rate %',
                    data: selectedData.map((item) =>
                        calculatePercentage(item.tripRate || 0, totalRate)
                    ),
                    backgroundColor: '#8B0000',
                    borderColor: '#8B0000',
                    borderWidth: 1,
                    barThickness: 20,
                },
            ],
            title: chartTitle,
            rawData: selectedData.map((item) => ({
                ...item,
                percentage: calculatePercentage(item.tripRate || 0, totalRate),
            })),
        };
    }, [direction, vm]);

    const yAxisMax = useMemo(() => {
        const percentages = chartData.datasets[0]?.data as number[] | undefined;
        const highestBar = percentages && percentages.length > 0
            ? Math.max(...percentages)
            : 0;

        if (highestBar <= 0) {
            return 10;
        }

        // Round up to a sensible whole-percent headroom so bars don't touch the top.
        return Math.ceil(highestBar * 1.1);
    }, [chartData]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: {
                display: true,
                text: chartData.title,
                font: { size: 14, weight: 'bold' as const },
                padding: 10,
            },
        },
        scales: {
            x: {
                title: { display: true, text: 'Time Periods' },
                ticks: { maxRotation: 45, minRotation: 45 },
                grid: { color: '#e0e0e0' },
            },
            y: {
                title: { display: true, text: 'Percentage' },
                beginAtZero: true,
                max: yAxisMax,
                ticks: {
                    stepSize: 1,
                    callback: (value: any) => value + '%',
                },
                grid: { color: '#e0e0e0' },
            },
        },
    };

    const handleExportJPEG = () => {
        const chart = chartRef.current;
        if (!chart) return;

        const canvas = chart.canvas as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const extraHeight = 20 + chartData.rawData.length * 16;
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = canvas.width;
        exportCanvas.height = canvas.height + extraHeight;
        const exportCtx = exportCanvas.getContext('2d');
        if (!exportCtx) return;

        exportCtx.fillStyle = '#fff';
        exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        exportCtx.drawImage(canvas, 0, 0);

        exportCtx.font = '12px Arial';
        exportCtx.fillStyle = '#000';
        const startY = canvas.height + 20;

        exportCtx.fillText('Period | Days | Trip Rate | %', 10, startY);

        chartData.rawData.forEach((item, i) => {
            const y = startY + 16 * (i + 1);
            const text = `${item.period || ''} | ${item.numberOfDays || 0} | ${item.tripRate || 0} | ${item.percentage.toFixed(1)}%`;
            exportCtx.fillText(text, 10, y);
        });

        const image = exportCanvas.toDataURL('image/jpeg', 0.9);
        const link = document.createElement('a');
        link.download = `${chartData.title.replace(/\s+/g, '_')}.jpeg`;
        link.href = image;
        link.click();
    };

    const openDialog = () => {
        setDialogOpen(true);
    }

    const closeDialog = () => {
        setDialogOpen(false)
    }

    const exportGraph = (exportVm: ExportTripRateGraphViewModel) => {
        if (exportVm.exportType == "JPEG") {
            handleExportJPEG();
            closeDialog()
            return;
        }

        exportVm.tripRate = vm;

        postPDF(`PDFGenerator`, `GetTripRateGraphPDF`, exportVm.fileName ?? "Trip Rate Graph", exportVm.displayToScreen, exportVm)
    }


    return (
        <Dialog open={open} fullWidth maxWidth="xl" onClose={onClose}>
            <Grid container>
                <Grid size={12}>
                    <Directions setType={setDirection} type={direction} />
                </Grid>
                <Grid>
                    <Button fullWidth={false} onClick={openDialog}>Export</Button>
                </Grid>
            </Grid>

            <div style={{ padding: '1rem' }}>
                <div style={{ position: 'relative', height: '80vh' }}>
                    <Bar ref={chartRef} data={chartData} options={options} />
                </div>
            </div>
            {dialogOpen && < ExportTripRateGraphDialog dialogOpen={dialogOpen} exportTripRate={exportGraph} handleClose={closeDialog} />}
        </Dialog>
    );
}
